<script lang="ts">
	import { get } from 'svelte/store';
	import { devices } from 'src/stores/lists/devices';
	import { isThreeEM, load_image_from, real_id } from '../../helpers/interface_helpers';
	import { convertValueAndUnit } from '../../helpers/utilities';

	import type { GetStatsResponseHistoryItemData } from '../../types/requests/charts/get_stats_period';
	import { ListItem, Scroll, Stack, SystemStyles } from '@Shelly/shelly-design-system';
	import { beforeUpdate } from 'svelte';
	import ConsumptionContainer from './ConsumptionContainer.svelte';
	import { setSidePanelState } from '../Sidepanel/sidepanel-helpers';

	export let roomID: number,
		totalRoomConsuption: number,
		totalReversedConsumption: number,
		roomDevices: GetStatsResponseHistoryItemData[],
		dummyNetMetering = false,
		units = 'Wh';

	const activeDevices = [],
		onDeviceClick = (id: string) => {
			setSidePanelState({
				id,
				type: 'device',
				open: true,
			});
		};

	beforeUpdate(() => {
		const allDevices = get(devices);
		for (let deviceID in roomDevices) {
			let deleted = false;
			if (roomDevices[deviceID]?.consumption > 0 || roomDevices[deviceID]?.reversed > 0) {
				const deviceRealID = real_id(deviceID),
					device = allDevices[deviceID] || allDevices[deviceRealID],
					deviceData = roomDevices[deviceID];
				let title = '';
				if (device) {
					title = device.name;
					if (isThreeEM(device?.type) && device?.mode != 'monophase') {
						const exist3EM = activeDevices.find((i) => i.deviceID == deviceRealID);
						if (exist3EM) {
							exist3EM.consumption += deviceData.consumption;
							exist3EM.reversed += deviceData.reversed;
							continue;
						}
					} else if (
						deviceID.indexOf('_') >= 0 &&
						activeDevices.findIndex((i) => i.title == title) >= 0
					) {
						const channel = deviceID.substring(deviceID.indexOf('_') + 1);
						title += ` (${Number(channel) + 1} channel)`;
					}
				} else {
					title = `Deleted Device id: ${deviceID}`;
					if (deviceID.indexOf('_') >= 0) {
						const split = deviceID.split('_');
						if (split[1]) {
							title = `Deleted Device id: ${split[0]} (${Number(split[1]) + 1} channel)`;
						}
					}
					deleted = true;
				}
				activeDevices.push({
					title,
					deviceID:
						isThreeEM(device?.type) && device?.mode != 'monophase'
							? deviceRealID
							: device?.id || deviceID,
					no_room_cons: device?.room_id == roomID && device?.no_room_cons,
					consumption: deviceData.consumption,
					reversed: deviceData.reversed,
					icon: device?.icon ? device.icon : undefined,
					image: device?.image ? load_image_from(device.image, device?.server) : '',
					backgroundColor: device?.backgroundColor,
					deleted,
				});
			}
		}
		activeDevices.sort((a: any, b: any) => {
			return b.consumption - a.consumption;
		});
	});
</script>

{#if activeDevices.length > 0}
	<div class="mt-m">
		<ConsumptionContainer noWrapperContainer={true}>
			<Scroll style="height:auto; max-height:270px">
				<Stack gap="s">
					{#each activeDevices as device}
						{#if device.consumption > 0}
							{@const deviceConsumption = dummyNetMetering
								? Math.max(0, device.consumption - device.reversed)
								: device.consumption}
							{@const consumption = convertValueAndUnit(deviceConsumption, units)}
							<ListItem
								uniqueId={device.deviceID}
								title={device.title}
								icon={device.icon}
								image={device.image
									? load_image_from(device.image, device?.server)
									: undefined}
								backgroundColor={device.backgroundColor}
								progress={{
									percent: device.no_room_cons
										? 0
										: (deviceConsumption / totalRoomConsuption) * 100,
									title: `${consumption.value} ${consumption.unit}`,
									color: device.no_room_cons ? 'white' : 'primary',
									showPercentBar: true,
									icon: 'fas fa-bolt',
									iconColor: device.no_room_cons
										? null
										: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,

									...(!device.no_room_cons && {
										progressColor: 'gradient-chart-consumed-energy',
									}),
								}}
								actionBtn={!device.deleted}
								on:click={() => {
									onDeviceClick(device.deviceID);
								}}
							/>
						{/if}
						{#if device.reversed > 0 && !dummyNetMetering}
							{@const reversed = convertValueAndUnit(device.reversed, units)}
							<ListItem
								title={device.title}
								image={device.image
									? load_image_from(device.image, device?.server)
									: undefined}
								icon={device.icon}
								backgroundColor={device.backgroundColor}
								progress={{
									percent: device.no_room_cons
										? 0
										: (device.reversed / Math.abs(totalReversedConsumption)) * 100,
									title: `${reversed.value} ${reversed.unit}`,
									color: 'solar',
									showPercentBar: true,
									icon: 'fas fa-bolt',
									iconColor: SystemStyles.COLOR_SOLAR,
								}}
								actionBtn={!device.deleted}
								on:click={() => {
									onDeviceClick(device.deviceID);
								}}
							/>
						{/if}
					{/each}
				</Stack>
			</Scroll>
		</ConsumptionContainer>
	</div>
{/if}
